import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, X, ShieldAlert, Clock, CircleDollarSign } from "lucide-react";
import { partnersApi, type CreditRequestStatus } from "@/services/b2b";
import { useAuthStore } from "@/store/authStore";
import { useCan } from "@/auth/permissions";
import { PageShell, Pill, EmptyState, LoadingState } from "@/shared/ui/PageHeader";
import { MetricCard } from "@/shared/ui/Card";
import { formatCents } from "@/services/finance";

/**
 * W3 · 临时额度审批队列
 *  - 仅 owner / tech_admin / tech_admin 可见（route guard + BE 403）
 *  - 复用 D3B pattern: 老板审批临时额度（Q1C 全审）
 *  - tech_admin 可看 / 不可批（BE: decide 仅 owner+tech_admin）
 */
export function CreditApprovalQueuePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<CreditRequestStatus>("pending");
  const [noteByRid, setNoteByRid] = useState<Record<string, string>>({});

  // 权限点判断（BE 也 403 防护）：能审额度 = channel_partners.quotas.approve
  const canDecide = useCan("channel_partners.quotas.approve");
  const allowed = !!user && canDecide;

  if (!allowed) {
    return (
      <PageShell title="临时额度审批队列" subtitle="无权限">
        <EmptyState message="仅老板 / 超级管理员 / 技术管理员可访问审批队列" />
        <button
          onClick={() => navigate("/")}
          className="mt-4 inline-flex h-9 items-center rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700"
        >
          返回首页
        </button>
      </PageShell>
    );
  }

  const queue = useQuery({
    queryKey: ["b2b", "approval-queue", tab],
    queryFn: () => partnersApi.listAllCreditRequests(tab),
  });

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const decide = useMutation({
    mutationFn: async ({
      rid,
      approve,
      note,
    }: {
      rid: string;
      approve: boolean;
      note?: string;
    }) => {
      const r = await askReason({
        scene: "credit_decision", group: approve ? "approve" : "reject", required: !approve, danger: !approve,
        title: approve ? "批准临时额度" : "拒绝临时额度", subtitle: undefined,
        confirmText: approve ? "确认批准" : "确认拒绝",
      });
      if (!r) throw new ReasonCancelled();
      // 行内「批注」输入框的字是补充说明：弹窗里没另写时用它
      return partnersApi.decideCreditQueue(rid, approve, { ...r, ...(r.reason || !note ? {} : { reason: note }) });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2b", "approval-queue"] });
      queryClient.invalidateQueries({ queryKey: ["b2b", "stats"] });
      queryClient.invalidateQueries({ queryKey: ["b2b", "partners"] });
    },
  });

  const rows = queue.data ?? [];
  const totalRequested = rows.reduce((sum, r) => sum + r.additional_credit_cents, 0);
  const avgDuration = rows.length
    ? Math.round(rows.reduce((s, r) => s + r.duration_days, 0) / rows.length)
    : 0;
  const oldest = rows.length
    ? rows.reduce((p, r) => (r.submitted_at < p ? r.submitted_at : p), rows[0].submitted_at)
    : null;
  const oldestHours = oldest
    ? Math.max(0, Math.round((Date.now() - new Date(oldest).getTime()) / 3_600_000))
    : 0;

  return (
    <PageShell
      title="临时额度审批队列"
      subtitle={canDecide ? "复用 D3B 老板审批模式" : "只读视图（技术管理员）"}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          label="待审申请"
          value={tab === "pending" ? `${rows.length}` : "—"}
          hint={canDecide ? "需立即处理" : "等待老板审批"}
        />
        <MetricCard
          label="申请金额合计"
          value={tab === "pending" ? formatCents(totalRequested) : "—"}
        />
        <MetricCard
          label="平均时长 (天)"
          value={tab === "pending" ? `${avgDuration}` : "—"}
        />
        <MetricCard
          label="最久候时"
          value={tab === "pending" ? `${oldestHours}h` : "—"}
          hint={oldestHours > 24 ? "超 24 小时" : undefined}
        />
      </div>

      <div className="flex gap-1.5 border-b border-slate-200">
        {(["pending", "approved", "rejected"] as CreditRequestStatus[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              "px-3 py-2 text-[13px] font-medium border-b-2 transition " +
              (tab === t
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-600 hover:text-slate-900")
            }
          >
            {t === "pending" ? "待审" : t === "approved" ? "已批" : "已拒"}
          </button>
        ))}
      </div>

      {queue.isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          message={
            tab === "pending"
              ? "无待审申请 · 队列空闲"
              : tab === "approved"
                ? "暂无已批申请记录"
                : "暂无已拒申请记录"
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">客户</th>
                <th className="px-4 py-3 text-right">申请额度</th>
                <th className="px-4 py-3 text-left">理由</th>
                <th className="px-4 py-3 text-right">期限</th>
                <th className="px-4 py-3 text-left">候时</th>
                <th className="px-4 py-3 text-left">状态</th>
                {tab === "pending" && canDecide && (
                  <th className="px-4 py-3 text-right">操作</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const submitted = new Date(r.submitted_at);
                const hours = Math.max(0, Math.round((Date.now() - submitted.getTime()) / 3_600_000));
                const isHot = hours > 24;
                return (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 align-top hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <a
                        href={`/admin/channel-partners/${r.partner_id}`}
                        className="font-semibold text-slate-900 hover:text-blue-600"
                      >
                        {r.partner_id.slice(0, 8)}…
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-slate-900">
                        +{formatCents(r.additional_credit_cents)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-md">
                      <div className="line-clamp-2">{r.reason}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {r.duration_days} 天
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-flex items-center gap-1 text-[12px] " +
                          (isHot ? "font-semibold text-rose-600" : "text-slate-500")
                        }
                      >
                        <Clock className="h-3 w-3" /> {hours}h
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Pill
                        tone={
                          r.status === "approved"
                            ? "success"
                            : r.status === "rejected"
                              ? "danger"
                              : "warning"
                        }
                      >
                        {r.status === "pending" ? "待审" : r.status === "approved" ? "已批" : "已拒"}
                      </Pill>
                    </td>
                    {tab === "pending" && canDecide && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          <input
                            placeholder="批注（可选）"
                            value={noteByRid[r.id] ?? ""}
                            onChange={(e) =>
                              setNoteByRid({ ...noteByRid, [r.id]: e.target.value })
                            }
                            className="h-8 w-32 rounded-md border border-slate-200 px-2 text-[12px] outline-none focus:border-blue-500"
                          />
                          <button
                            type="button"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                rid: r.id,
                                approve: true,
                                note: noteByRid[r.id] || undefined,
                              })
                            }
                            className="inline-flex h-8 items-center gap-1 rounded-md bg-emerald-600 px-2.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" /> 批准
                          </button>
                          <button
                            type="button"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ rid: r.id, approve: false, note: noteByRid[r.id] || undefined })}
                            className="inline-flex h-8 items-center gap-1 rounded-md bg-rose-600 px-2.5 text-[12px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" /> 拒绝
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {decide.isError && !isReasonCancelled(decide.error) && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-700">
          <ShieldAlert className="h-3.5 w-3.5" />
          审批失败：{(decide.error as Error).message}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600">
        <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
          <CircleDollarSign className="h-3.5 w-3.5" />
          复用 D3B 模式 · Q1C 全审
        </span>
        <p className="mt-1">
          通过的临时额度自动累加到客户 credit_limit 上 · 拒绝必须选原因 · 历史可在「已批」/「已拒」tab 查看。
        </p>
      </div>
    {reasonDialog}
    </PageShell>
  );
}
